import axios from 'axios'
import type { ReadStateUndoData, UserDocListItem } from './interfaces'
import { DocView } from './doc'
import type { DocsView } from './doc'
import { appNotifications } from '@/services/notification.app'
import { Sentry } from '@/services/sentry'

/**
 * Mark the doc finished, then show a confirmation toast with an Undo button.
 *
 * Like the doc menus, this takes either a `DocView` (on a single doc page), or
 * a list item with its `docsView` (on a listing page).
 */
export async function markDocFinishedWithUndo(
  doc: UserDocListItem | DocView,
  docsView?: DocsView,
): Promise<void> {
  const undoData =
    doc instanceof DocView
      ? await doc.moveTo('finished')
      : await docsView?.moveTo(doc, 'finished')
  if (undoData) {
    confirmReadStateChange('Marked as Finished', undoData, doc, docsView)
  }
}

/**
 * Mark the doc not started, then show a confirmation toast with an Undo button.
 *
 * Like the doc menus, this takes either a `DocView` (on a single doc page), or
 * a list item with its `docsView` (on a listing page).
 */
export async function markDocNotStartedWithUndo(
  doc: UserDocListItem | DocView,
  docsView?: DocsView,
): Promise<void> {
  const undoData =
    doc instanceof DocView
      ? await doc.markNotStarted()
      : await docsView?.markNotStarted(doc)
  if (undoData) {
    confirmReadStateChange('Marked as Not Started', undoData, doc, docsView)
  }
}

/**
 * Confirm "Mark as finished" or "Mark as not started" with a toast that has
 * an "Undo" button.
 *
 * The toast keeps track of the doc and its view, so Undo still works after
 * the doc's card is gone, e.g. if it leaves "Reading Now".
 *
 * @param message - The confirmation, e.g. "Marked as Finished".
 * @param undoData - The data to undo the change.
 * @param doc - The doc that was changed: a `DocView`, or a list item with its
 *   `docsView`.
 * @param docsView - The doc's list, on a listing page.
 */
function confirmReadStateChange(
  message: string,
  undoData: ReadStateUndoData,
  doc: UserDocListItem | DocView,
  docsView?: DocsView,
): void {
  let isUndoing = false
  const id = appNotifications.unique({
    message,
    compact: true,
    actions: [
      {
        label: 'Undo',
        onClick: async (): Promise<void> => {
          // Ignore repeated clicks while the request is pending.
          if (isUndoing) {
            return
          }
          isUndoing = true

          try {
            if (doc instanceof DocView) {
              await doc.undoReadStateChange(undoData)
            } else {
              await docsView?.undoReadStateChange(doc, undoData)
            }
            appNotifications.dismiss(id)
          } catch (error) {
            // Showing the error replaces this toast.
            if (isStaleUndoError(error)) {
              appNotifications.error({
                message: 'This change can no longer be undone.',
              })
            } else {
              Sentry.captureException(error)
              appNotifications.error({ message: "Couldn't undo the change." })
            }
          }
        },
      },
    ],
  })
}

/**
 * Whether the Undo request failed because the doc changed since (409).
 */
function isStaleUndoError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409
}
